import { useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import KpiCard from '@/components/KpiCard';
import DateRangeFilter from '@/components/DateRangeFilter';
import { fetchStats, fetchFunnel, fetchOwnerPerformance, fetchWinLossService, fetchAging, fetchRevenueForecast, scoreAllOpen } from '@/lib/api';
import { formatINR, formatNumber } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, ReferenceLine, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { Printer } from 'lucide-react';

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_sales-analytics-base/artifacts/bju11je1_Final%20Logo-01.webp";

export default function Dashboard() {
    const [range, setRange] = useState({ from: null, to: null });
    const [stats, setStats] = useState(null);
    const [funnel, setFunnel] = useState([]);
    const [owners, setOwners] = useState([]);
    const [services, setServices] = useState([]);
    const [aging, setAging] = useState([]);
    const [forecast, setForecast] = useState({ history: [], forecast: [] });
    const [scoring, setScoring] = useState(false);

    const load = async () => {
        const params = {};
        if (range.from) params.date_from = range.from;
        if (range.to) params.date_to = range.to;
        const [s, f, o, sv, a, rev] = await Promise.all([
            fetchStats(params), fetchFunnel(params), fetchOwnerPerformance(params),
            fetchWinLossService(params), fetchAging(params), fetchRevenueForecast({ months_history: 6, months_forecast: 3 }),
        ]);
        setStats(s); setFunnel(f); setOwners(o); setServices(sv); setAging(a); setForecast(rev);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

    const runAiScoring = async () => {
        setScoring(true);
        try {
            const r = await scoreAllOpen(15);
            toast.success(`AI scored ${r.scored} leads`);
        } catch { toast.error('AI scoring failed'); }
        finally { setScoring(false); }
    };

    const printDashboard = () => {
        document.body.classList.add('printing-dashboard');
        const cleanup = () => {
            document.body.classList.remove('printing-dashboard');
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        setTimeout(() => window.print(), 50);
    };
    const printedOn = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    // Build a query-string suffix for /leads links that preserves the current date range
    const leadsQS = (extra = '') => {
        const parts = [];
        if (extra) parts.push(extra);
        if (range.from) parts.push(`date_from=${encodeURIComponent(range.from)}`);
        if (range.to) parts.push(`date_to=${encodeURIComponent(range.to)}`);
        return parts.length ? `?${parts.join('&')}` : '';
    };

    if (!stats) return <div className="p-8 text-slate-500">Loading…</div>;

    const funnelColors = ['#3d3d3d', '#3b5470', '#4a5b8c', '#8a6d2d', '#a35a2a', '#2d6b3d', '#8a2d2d'];
    const maxCount = Math.max(...funnel.map(f => f.count), 1);

    // build combined series for forecast chart
    const forecastData = [
        ...forecast.history.map(h => ({ month: h.month, actual: h.value, forecast: null })),
        // bridge point for continuous line: last historical point also as start of forecast
        ...(forecast.history.length && forecast.forecast.length
            ? [{ month: forecast.history[forecast.history.length - 1].month, actual: null, forecast: forecast.history[forecast.history.length - 1].value }]
            : []),
        ...forecast.forecast.map(f => ({ month: f.month, actual: null, forecast: f.value })),
    ];
    const totalForecast = forecast.forecast.reduce((s, f) => s + f.value, 0);

    return (
        <div>
            <PageHeader
                title="Executive Dashboard"
                subtitle="Pipeline Health · FY 2026"
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <DateRangeFilter value={range} onChange={setRange} />
                        <Button
                            onClick={printDashboard}
                            data-testid="btn-print-dashboard"
                            variant="outline"
                            className="border-[#E2E8F0] bg-white hover:bg-slate-50 gap-2"
                        >
                            <Printer className="w-4 h-4" /> Print
                        </Button>
                        <Button
                            onClick={runAiScoring}
                            disabled={scoring}
                            data-testid="btn-ai-score-all"
                            className="bg-[#016a6d] hover:bg-[#014e50] text-white gap-2"
                        >
                            <Sparkles className="w-4 h-4" />
                            {scoring ? 'Scoring…' : 'AI Score Leads'}
                        </Button>
                    </div>
                }
            />

            <div className="p-8 space-y-6">
                {/* Print-only header — hidden on screen, shown in print */}
                <div className="print-block hidden">
                    <div className="print-header items-center justify-between pb-4 mb-4 border-b border-slate-300">
                        <img src={LOGO_URL} alt="Ikas Corp" className="h-10 w-auto" />
                        <div className="text-right">
                            <div className="font-display text-lg font-bold text-slate-900">Executive Dashboard · Pipeline Health</div>
                            <div className="text-xs text-slate-500">Generated {printedOn} · Ikas Corp Sales CRM</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 print-block" data-testid="kpi-grid">
                    <KpiCard label="Total Leads" value={formatNumber(stats.total_leads)} testId="kpi-total-leads" to={`/leads${leadsQS()}`} />
                    <KpiCard label="Open" value={formatNumber(stats.open_leads)} testId="kpi-open" accent="text-[#189290]" to={`/leads${leadsQS('status=Open')}`} />
                    <KpiCard label="Won" value={formatNumber(stats.won)} testId="kpi-won" accent="text-[#34C759]" to={`/leads${leadsQS('win_loss=Won')}`} />
                    <KpiCard label="Lost" value={formatNumber(stats.lost)} testId="kpi-lost" accent="text-[#FF453A]" to={`/leads${leadsQS('win_loss=Lost')}`} />
                    <KpiCard label="Win Ratio" value={`${stats.win_loss_ratio}%`} testId="kpi-win-ratio" />
                    <KpiCard label="Upcoming" value={formatNumber(stats.upcoming)} testId="kpi-upcoming" accent="text-[#FF9F0A]" to={`/leads${leadsQS('win_loss=Upcoming Months')}`} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print-block">
                    <KpiCard label="Pipeline Value" value={stats.pipeline_value} isCurrency testId="kpi-pipeline-value" to={`/leads${leadsQS('status=Open')}`} />
                    <KpiCard label="Weighted Pipeline" value={stats.weighted_pipeline} isCurrency testId="kpi-weighted" accent="text-[#016a6d]" to={`/leads${leadsQS('status=Open')}`} />
                    <KpiCard label="Won Value" value={stats.won_value} isCurrency testId="kpi-won-value" accent="text-[#34C759]" to={`/leads${leadsQS('win_loss=Won')}`} />
                    <KpiCard label="Avg Deal Size" value={stats.avg_deal_size} isCurrency testId="kpi-avg-deal" />
                </div>

                {/* Revenue Forecast */}
                <div className="surface-1 rounded-md p-6 print-block" data-testid="forecast-widget">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <div>
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-[#016a6d]" />
                                <div className="overline">Revenue Forecast</div>
                            </div>
                            <h3 className="font-display text-xl font-bold text-slate-900 mt-1">Won Revenue · 6mo history + 3mo linear projection</h3>
                        </div>
                        <div className="text-right">
                            <div className="overline">3mo Projected</div>
                            <div className="font-display text-2xl font-bold text-[#016a6d] tabular-nums mt-1" data-testid="forecast-total">{formatINR(totalForecast)}</div>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={forecastData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis dataKey="month" stroke="#666" fontSize={11} />
                            <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => formatINR(v)} />
                            <Tooltip
                                contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 6 }}
                                formatter={(v, name) => v == null ? ['—', name] : [formatINR(v), name === 'actual' ? 'Actual Won' : 'Forecast']}
                            />
                            {forecast.history.length > 0 && (
                                <ReferenceLine x={forecast.history[forecast.history.length - 1].month} stroke="#016a6d" strokeDasharray="4 4" opacity={0.5} label={{ value: 'Today', fill: '#016a6d', fontSize: 10, position: 'top' }} />
                            )}
                            <Line type="monotone" dataKey="actual" stroke="#34C759" strokeWidth={2} dot={{ r: 3, fill: '#34C759' }} connectNulls={false} name="Actual Won" />
                            <Line type="monotone" dataKey="forecast" stroke="#016a6d" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3, fill: '#016a6d' }} connectNulls={false} name="Forecast" />
                        </LineChart>
                    </ResponsiveContainer>
                    {forecastData.length === 0 && (
                        <div className="text-center text-sm text-slate-500 py-6">Not enough won-deal history yet — close a few deals to see trend projections.</div>
                    )}
                </div>

                {/* Funnel + Aging */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="surface-1 rounded-md p-6 lg:col-span-2 print-block" data-testid="funnel-widget">
                        <div className="overline">Pipeline Funnel</div>
                        <h3 className="font-display text-xl font-bold text-slate-900 mt-1 mb-6">Leads by Stage</h3>
                        <div className="space-y-3">
                            {funnel.map((s, i) => {
                                const pct = (s.count / maxCount) * 100;
                                return (
                                    <div key={s.stage} data-testid={`funnel-row-${i}`}>
                                        <div className="flex justify-between items-baseline mb-1.5">
                                            <span className="text-sm text-slate-700">{s.stage}</span>
                                            <div className="flex items-center gap-3 tabular-nums">
                                                <span className="text-xs text-slate-500">{s.count} leads</span>
                                                <span className="text-sm text-slate-900 font-medium">{formatINR(s.value)}</span>
                                            </div>
                                        </div>
                                        <div className="h-2 bg-[#FFFFFF] rounded-sm overflow-hidden">
                                            <div className="h-full transition-all" style={{ width: `${pct}%`, background: funnelColors[i] || '#016a6d' }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="surface-1 rounded-md p-6 print-block" data-testid="aging-widget">
                        <div className="overline">Pipeline Aging</div>
                        <h3 className="font-display text-xl font-bold text-slate-900 mt-1 mb-4">Open Deals</h3>
                        <div className="space-y-3">
                            {aging.map((a, i) => (
                                <div key={a.bucket} className="flex items-center justify-between border-b border-[#E2E8F0] last:border-0 pb-3 last:pb-0">
                                    <div>
                                        <div className="text-sm text-slate-700">{a.bucket}</div>
                                        <div className="text-xs text-slate-500 tabular-nums">{a.count} leads</div>
                                    </div>
                                    <div className={`text-sm tabular-nums font-medium ${i >= 2 ? 'text-[#FF9F0A]' : 'text-slate-900'}`}>
                                        {formatINR(a.value)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Owner + Win/Loss */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="surface-1 rounded-md p-6 print-block" data-testid="owner-performance">
                        <div className="overline">Sales Owner Performance</div>
                        <h3 className="font-display text-xl font-bold text-slate-900 mt-1 mb-4">Leaderboard</h3>
                        <div className="space-y-2">
                            {owners.map((o, i) => (
                                <div key={o.owner} className="flex items-center gap-3 py-2 border-b border-[#E2E8F0] last:border-0">
                                    <div className="w-7 h-7 rounded-full bg-[#FFFFFF] border border-[#E2E8F0] flex items-center justify-center text-xs font-medium text-slate-500 tabular-nums">
                                        {i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-slate-900 font-medium truncate">{o.owner}</div>
                                        <div className="text-xs text-slate-500 tabular-nums">
                                            {o.leads} leads · <span className="text-[#34C759]">{o.won}W</span> · <span className="text-[#FF453A]">{o.lost}L</span>
                                        </div>
                                    </div>
                                    <div className="text-right tabular-nums">
                                        <div className="text-sm text-slate-900">{formatINR(o.pipeline_value)}</div>
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Pipeline</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="surface-1 rounded-md p-6 print-block" data-testid="winloss-service">
                        <div className="overline">Win / Loss by Service Line</div>
                        <h3 className="font-display text-xl font-bold text-slate-900 mt-1 mb-4">Service Mix</h3>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={services} layout="vertical" margin={{ left: 20 }}>
                                <XAxis type="number" stroke="#666" fontSize={11} />
                                <YAxis type="category" dataKey="service_line" stroke="#888" fontSize={11} width={140} />
                                <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 6 }} />
                                <Bar dataKey="won" stackId="a" fill="#34C759" />
                                <Bar dataKey="lost" stackId="a" fill="#FF453A" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}
